import React from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  Shield,
  FileSignature,
  Cookie,
  RotateCcw,
  Truck,
  Copyright,
  Users,
  Gavel,
  Database,
  Settings,
  ArrowRight,
} from "lucide-react";

const HomePage: React.FC = () => {
  const documents = [
    {
      path: "/terms",
      title: "Terms & Conditions",
      description:
        "Our terms of service and user agreement that govern the use of Baakas platform.",
      icon: FileText,
      color: "bg-primary-500",
    },
    {
      path: "/privacy",
      title: "Privacy Policy",
      description:
        "How we collect, use, and protect your personal information and data.",
      icon: Shield,
      color: "bg-secondary-500",
    },
    {
      path: "/agreements",
      title: "Agreements",
      description:
        "Seller and buyer agreements with signature fields and form elements.",
      icon: FileSignature,
      color: "bg-accent-500",
    },
    {
      path: "/cookie-policy",
      title: "Cookie Policy",
      description:
        "Information about cookies and tracking technologies used on our platform.",
      icon: Cookie,
      color: "bg-orange-500",
    },
    {
      path: "/refund-policy",
      title: "Refund Policy",
      description: "Our refund and return policy for products and services.",
      icon: RotateCcw,
      color: "bg-green-500",
    },
    {
      path: "/shipping-policy",
      title: "Shipping Policy",
      description:
        "Shipping information, delivery times, and shipping restrictions.",
      icon: Truck,
      color: "bg-blue-500",
    },
    {
      path: "/intellectual-property-policy",
      title: "Intellectual Property Policy",
      description:
        "Policies regarding intellectual property rights and content ownership.",
      icon: Copyright,
      color: "bg-purple-500",
    },
    {
      path: "/code-of-conduct",
      title: "Code of Conduct",
      description:
        "Community guidelines and expected behavior on our platform.",
      icon: Users,
      color: "bg-indigo-500",
    },
    {
      path: "/dispute-resolution-policy",
      title: "Dispute Resolution Policy",
      description: "How we handle disputes and conflict resolution procedures.",
      icon: Gavel,
      color: "bg-red-500",
    },
    {
      path: "/data-processing-agreement",
      title: "Data Processing Agreement",
      description: "Agreement governing the processing of personal data.",
      icon: Database,
      color: "bg-teal-500",
    },
    {
      path: "/service-level-agreement",
      title: "Service Level Agreement",
      description:
        "Service availability, performance standards, and support commitments.",
      icon: Settings,
      color: "bg-gray-500",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <img
                src="/logo.png"
                alt="Baakas"
                className="w-16 h-16 object-contain"
              />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Baakas Legal Documents
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Access all legal documents, terms, policies, and agreements for
              the Baakas platform. All documents are printable and can be
              exported as PDF.
            </p>
          </div>
        </div>
      </div>

      {/* Documents Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.map((doc) => {
            const Icon = doc.icon;
            return (
              <Link
                key={doc.path}
                to={doc.path}
                className="group bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200 hover:border-primary-300"
              >
                <div className="flex items-start space-x-4">
                  <div
                    className={`${doc.color} p-3 rounded-lg group-hover:scale-110 transition-transform duration-200`}
                  >
                    <Icon className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {doc.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{doc.description}</p>
                    <div className="flex items-center text-primary-600 font-medium group-hover:text-primary-700">
                      <span>View Document</span>
                      <ArrowRight
                        className="ml-2 group-hover:translate-x-1 transition-transform"
                        size={16}
                      />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Document Features
            </h2>
            <p className="text-lg text-gray-600">
              Our legal documents are designed for clarity, accessibility, and
              professional use. Each document is available as a separate page
              for easy navigation and reference.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FileText className="text-primary-600" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Print-Ready Format
              </h3>
              <p className="text-gray-600">
                All documents are formatted for professional printing with
                proper margins and typography.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Shield className="text-secondary-600" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                PDF Export
              </h3>
              <p className="text-gray-600">
                Download any document as a PDF for offline use, archiving, or
                sharing.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FileSignature className="text-accent-600" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Signature Fields
              </h3>
              <p className="text-gray-600">
                Agreements include signature fields and form elements for easy
                completion.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <p className="text-gray-600 mb-2">
              © 2024 Baakas. All rights reserved.
            </p>
            <p className="text-sm text-gray-500">
              For questions about these documents, contact us at{" "}
              <a
                href="mailto:support@baakas.com.np"
                className="text-primary-600 hover:text-primary-700"
              >
                support@baakas.com.np
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
